import { jsPDF } from 'jspdf';
import ExceptionLogger from "../ExceptionHelper";
import JSZip from 'jszip';
import saveAs from 'file-saver';

// Helper to collect all messages from a conversation paginator
async function getAllMessages(conversation) {
    const messages = [];
    try {
        let page = await conversation.getMessages(undefined, undefined, "forward");
        while (page) {
            for (const msg of page.items) {
                messages.push(msg);
            }
            if (!page.hasNextPage) break;
            page = await page.nextPage();
        }
    } catch (e) {
        ExceptionLogger(e, "generatePdfTranscript.js:getAllMessages", "Error fetching messages from conversation", conversation?.sid || "", null);
        throw e;
    }
    return messages;
}

// Build identity → friendlyName map from conversation participants
async function buildUserNameMap(conversation) {
    const map = {};
    try {
        const participants = await conversation.getParticipants();
        await Promise.all(participants.map(async (p) => {
            try {
                const user = await p.getUser();
                if (user && user.friendlyName) {
                    map[p.identity] = user.friendlyName;
                } else if (p.identity) {
                    map[p.identity] = p.identity;
                }
            } catch (e) {
                // If we can't get the user, fall back to identity
                if (p.identity) map[p.identity] = p.identity;
            }
        }));
    } catch (e) {
        ExceptionLogger(e, "generatePdfTranscript.js:buildUserNameMap", "Error fetching participants", conversation?.sid || "", null);
    }
    return map;
}

// Convert a Date's toDateString() to slugified form (spaces → hyphens), matching WebChatClient
function slugifyDate(date) {
    return date.toDateString().replace(/\s+/g, '-');
}

async function getMediaInfo(messages) {
    const mediaInfo = [];
    try {
        for (const message of messages || []) {
            const attached = message.attachedMedia || message.media || [];
            for (const media of attached) {
                try {
                    const filename = media.filename || media.fileName || `attachment-${Date.now()}`;
                    let url = null;
                    if (typeof media.getContentTemporaryUrl === 'function') {
                        url = await media.getContentTemporaryUrl();
                    } else if (media.url) {
                        url = media.url;
                    }

                    if (url) {
                        // fetch blob
                        try {
                            const resp = await fetch(url);
                            if (!resp.ok) throw new Error(`Failed to fetch media: ${resp.statusText}`);
                            const blob = await resp.blob();
                            mediaInfo.push({ url, filename, blob, type: blob.type });
                        } catch (e) {
                            ExceptionLogger(e, "generatePdfTranscript.js:getMediaInfo", "Failed fetching media blob", message?.sid || message?.index || "", { url });
                        }
                    }
                } catch (e) {
                    ExceptionLogger(e, "generatePdfTranscript.js:getMediaInfo", "Error processing media info", message?.sid || message?.index || "", null);
                }
            }
        }
    } catch (e) {
        ExceptionLogger(e, "generatePdfTranscript.js:getMediaInfo", "Failed collecting media info", null, null);
    }
    return mediaInfo;
}

export async function generatePdfTranscript(manager, conversationSid, customerName) {
    try {
        if (!manager || !manager.conversationsClient) {
            throw new Error('Manager or conversationsClient unavailable');
        }

        const conversation = await manager.conversationsClient.getConversationBySid(conversationSid);
        if (!conversation) throw new Error('Conversation not found');

        const [messages, userNameMap] = await Promise.all([
            getAllMessages(conversation),
            buildUserNameMap(conversation)
        ]);
        const mediaInfo = await getMediaInfo(messages);

        // Resolve friendly names — fall back to raw identity if not found
        const resolveName = (identity) => userNameMap[identity] || identity || '';

        // Determine customer name and unique agent names (WebChatClient pattern)
        const resolvedCustomerName = customerName
            || conversation.friendlyName
            || (messages.length > 0 ? resolveName(messages[0].author) : 'Customer');

        const allAuthors = [...new Set(messages.map(m => resolveName(m.author)).filter(Boolean))];
        const agentNames = allAuthors.filter(name => name !== resolvedCustomerName);

        // Build title: "Conversation with {customer} and {agent1} and {agent2}"
        let title = `Conversation with ${resolvedCustomerName}`;
        agentNames.forEach(name => { title += ` and ${name}`; });

        const doc = new jsPDF({ unit: 'pt' });
        const margin = 40;
        const pageWidth = doc.internal.pageSize.getWidth();
        const usableWidth = pageWidth - margin * 2;
        let cursorY = 60;

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin, cursorY);
        doc.setFont('helvetica', 'normal');
        cursorY += 24;

        const conversationDate = conversation.dateCreated || (messages.length > 0 ? messages[0].dateCreated : new Date());
        const dateStr = `Date: ${conversationDate.toLocaleString('default', { dateStyle: 'long' })}`;
        doc.setFontSize(10);
        doc.text(dateStr, margin, cursorY);
        cursorY += 14;

        // Duration: from conversation.dateCreated to last message date
        try {
            const start = conversation.dateCreated || messages[0]?.dateCreated;
            const last = messages.length > 0 ? messages[messages.length - 1].dateCreated : start;
            const seconds = Math.round(((last?.getTime?.() ?? new Date(last).getTime()) - (start?.getTime?.() ?? new Date(start).getTime())) / 1000);
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            const durationStr = mins > 0 ? `Duration: ${mins}m ${secs}s` : `Duration: ${secs} seconds`;
            doc.text(durationStr, margin, cursorY);
            cursorY += 18;
        } catch (e) {
            ExceptionLogger(e, "generatePdfTranscript.js", "Failed calculating duration", conversation?.sid || conversationSid, null);
        }

        doc.setFontSize(10);
        cursorY += 6;

        const lineHeight = 12;
        const wrap = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

        for (const message of messages) {
            try {
                const timestamp = message.dateCreated
                    ? `${String(message.dateCreated.getHours()).padStart(2, '0')}:${String(message.dateCreated.getMinutes()).padStart(2, '0')}`
                    : '';
                const author = resolveName(message.author);
                const body = message.body || message.source?.body || '';

                const header = `${timestamp}  ${author}:`;
                const headerLines = wrap(header, usableWidth);
                for (const line of headerLines) {
                    if (cursorY + lineHeight > doc.internal.pageSize.getHeight() - margin) {
                        doc.addPage();
                        cursorY = margin;
                    }
                    doc.setFont('helvetica', 'bold');
                    doc.text(line, margin, cursorY);
                    doc.setFont('helvetica', 'normal');
                    cursorY += lineHeight;
                }

                const bodyLines = wrap(body, usableWidth);
                for (const line of bodyLines) {
                    if (cursorY + lineHeight > doc.internal.pageSize.getHeight() - margin) {
                        doc.addPage();
                        cursorY = margin;
                    }
                    doc.text(line, margin + 8, cursorY);
                    cursorY += lineHeight;
                }

                cursorY += lineHeight / 2;
            } catch (e) {
                ExceptionLogger(e, "generatePdfTranscript.js", "Error formatting a message into PDF", conversation?.sid || conversationSid, { messageSid: message?.sid || null });
            }
        }

        // Use conversation SID as filename to avoid PII
        const pdfNameBase = conversationSid;
        const zipNameBase = conversationSid;

        const pdfBlob = doc.output('blob');

        if (mediaInfo.length > 0) {
            try {
                const zip = new JSZip();
                const folder = zip.folder(zipNameBase) || zip;
                folder.file(`${pdfNameBase}.pdf`, pdfBlob);

                const nameCounts = {};
                for (let i = 0; i < mediaInfo.length; i++) {
                    const info = mediaInfo[i];
                    let name = (info.filename || `attachment-${i}`).replace(/[^a-zA-Z0-9.\-_]/g, '_');
                    if (nameCounts[name]) {
                        const dot = name.lastIndexOf('.');
                        const base = dot > 0 ? name.substring(0, dot) : name;
                        const ext = dot > 0 ? name.substring(dot) : '';
                        name = `${base}-${nameCounts[name] + 1}${ext}`;
                    }
                    nameCounts[name] = (nameCounts[name] || 0) + 1;
                    if (info.blob) folder.file(name, info.blob);
                }

                const zipBlob = await zip.generateAsync({ type: 'blob' });
                saveAs(zipBlob, `${zipNameBase}.zip`);
            } catch (e) {
                ExceptionLogger(e, "generatePdfTranscript.js", "Failed creating ZIP with media and PDF", conversation?.sid || conversationSid, null);
                saveAs(pdfBlob, `${pdfNameBase}.pdf`);
            }
        } else {
            saveAs(pdfBlob, `${pdfNameBase}.pdf`);
        }
    } catch (e) {
        ExceptionLogger(e, "generatePdfTranscript.js", "Failed generating PDF transcript", conversationSid || "", null);
        throw e;
    }
}

export default generatePdfTranscript;
